import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { reverse } from 'named-urls';
import routes from '@achievable-me/tundra-wizard/routes';
import numeral from 'numeral';
import pluralize from 'pluralize';
import styles from './styles.scss';

const Trait = ({ name, pass, subtext }) => {
  const iconClass = pass ? 'fad fa-check' : 'fad fa-exclamation-triangle';

  return (
    <div className={styles.trait}>
      <i className={iconClass} />
      <div className={styles.traitBody}>
        <div className={styles.traitName}>{name}</div>
        <div className={styles.traitSubtext}>{subtext}</div>
      </div>
    </div>
  );
};

const getWordCountTrait = ({ scoreMeta }) => {
  const { wordCount = 0 } = scoreMeta;
  const pass = wordCount >= 800;
  const wordCountString = `${numeral(wordCount).format('0,0')} ${pluralize(
    'word',
    wordCount
  )}`;
  const subtext = `Writing a long essay of at least 800 words is the simplest and most important thing you can do to achieve a high score. We counted approximately ${wordCountString} in your essay.`;

  return <Trait name="Word count" subtext={subtext} pass={pass} />;
};

const getParagraphCountTrait = ({ scoreMeta }) => {
  const { paragraphCount = 0 } = scoreMeta;
  const pass = paragraphCount >= 4;
  const paragraphCountString = `${pluralize('paragraph', paragraphCount, true)}`;
  const subtext = `Writing a well-organized essay of at least four paragraphs is another easy way to boost your score. Start with a thesis paragraph, include several body paragraphs, and finish with a conclusion paragraph. We counted ${paragraphCountString} in your essay.`;

  return <Trait name="Paragraph count" subtext={subtext} pass={pass} />;
};

const getSpellingErrorsTrait = ({ scoreMeta }) => {
  const { spellingErrorRate = 0 } = scoreMeta;
  const pass = spellingErrorRate <= 0.1;
  const subtext = `You don't get points for spelling, but having too many spelling errors will detract from your score. Write quickly without editing much, and then do a final spelling and grammar proofreading pass when complete to achieve a high word count while keeping spelling errors to a minimum. Aim for less than 10% errors; we detected ${numeral(
    spellingErrorRate
  ).format('0%')} in your essay.`;

  return <Trait name="Spelling errors" subtext={subtext} pass={pass} />;
};

const getThesisSentenceTrait = ({ scoreMeta }) => {
  const { hasThesis = false } = scoreMeta;
  const pass = hasThesis;
  const thesisString = pass ? 'detected a' : 'did not detect a';
  const subtext = `Your thesis sentence is probably the single most important sentence in your entire essay – so make sure that it is explicit and unambiguous! It will be easiest for the e-rater to see your thesis when it's included as the last sentence in your first paragraph, and when it includes much of the same language as in the prompt. You should also consider writing your thesis statement before anything else, in the initial three minutes of the exercise. Our grader ${thesisString} thesis sentence in your essay.`;

  return <Trait name="Thesis sentence" subtext={subtext} pass={pass} />;
};

const getTransitionSentenceTrait = ({ scoreMeta }) => {
  const { hasTransitionSentences = false } = scoreMeta;
  const pass = hasTransitionSentences;
  const thesisString = pass ? 'detected' : 'did not detect';
  const subtext = `Remember to include clear topic/transition sentences at the beginning of each of your body paragraphs. The easiest way to do this is to refer to your arguments numerically (i.e., "first," "second," "third"). Easy fix, easy points. Our grader ${thesisString} enough transition sentences in your essay.`;

  return <Trait name="Transition sentences" subtext={subtext} pass={pass} />;
};

const getEvidentiarySentenceTrait = ({ scoreMeta }) => {
  const { hasEvidentiarySentences = false } = scoreMeta;
  const pass = hasEvidentiarySentences;
  const thesisString = pass ? 'detected' : 'did not detect';
  const subtext = `Strive to include one fabricated study in each of your body paragraphs, and lean on the DRI rubric as heavily as possible. Be sure to include lots of concrete detail (i.e., names, dates, statistics, etc.). Our grader ${thesisString} enough evidentiary statements in your essay.`;

  return <Trait name="Evidentiary sentences" subtext={subtext} pass={pass} />;
};

const getConclusionSentenceTrait = ({ scoreMeta }) => {
  const { hasConclusion = false } = scoreMeta;
  const pass = hasConclusion;
  const thesisString = pass ? 'detected a' : 'did not detect a';
  const subtext = `Make it simple for the e-grader to find your conclusion paragraph. Using the words "in conclusion" or "in summation" to your final paragraph may sound silly, but it's even sillier to leave such easy points on the table. Our grader ${thesisString} conclusion sentence in your essay.`;

  return <Trait name="Conclusion sentence" subtext={subtext} pass={pass} />;
};

const ScoringTraits = ({ scoreMeta }) => {
  const wordCountTrait = getWordCountTrait({ scoreMeta });
  const paragraphCountTrait = getParagraphCountTrait({ scoreMeta });
  const spellingErrorsTrait = getSpellingErrorsTrait({ scoreMeta });
  const thesisStatementTrait = getThesisSentenceTrait({ scoreMeta });
  const transitionSentenceTrait = getTransitionSentenceTrait({ scoreMeta });
  const evidentiarySentenceTrait = getEvidentiarySentenceTrait({ scoreMeta });
  const conclusionSentenceTrait = getConclusionSentenceTrait({ scoreMeta });

  return (
    <div>
      {wordCountTrait}
      {paragraphCountTrait}
      {spellingErrorsTrait}
      {thesisStatementTrait}
      {transitionSentenceTrait}
      {evidentiarySentenceTrait}
      {conclusionSentenceTrait}
    </div>
  );
};

const ETS_SCORE_DESCRIPTIONS = [
  "The examinee's analytical writing skills cannot be evaluated because the responses do not address any part of the assigned tasks, are merely attempts to copy the assignments, are in a foreign language or display only indecipherable text.",
  'Displays fundamental deficiencies in analytical writing. The writing is fundamentally flawed in at least one of the following ways: content that is extremely confusing or mostly irrelevant to the assigned tasks; little or no development; severe and pervasive errors that result in incoherence.',
  'Displays serious weaknesses in analytical writing. The writing is seriously flawed in at least one of the following ways: serious lack of analysis or development; lack of organization; serious and frequent problems in sentence structure or language usage, with errors that obscure meaning.',
  'Displays some competence in analytical writing, although the writing is flawed in at least one of the following ways: limited analysis or development; weak organization; weak control of sentence structure or language usage, with errors that often result in vagueness or lack of clarity.',
  'Provides competent analysis of ideas; develops and supports main points with relevant reasons and/or examples; is adequately organized; conveys meaning with reasonable clarity; demonstrates satisfactory control of sentence structure and language usage, but may have some errors that affect clarity.',
  'Provides generally thoughtful analysis of complex ideas; develops and supports main points with logically sound reasons and/or well-chosen examples; is generally focused and well organized; uses sentence variety and vocabulary to convey meaning clearly; demonstrates good control of sentence structure and language usage, but may have minor errors that do not interfere with meaning.',
  'Sustains insightful, in-depth analysis of complex ideas; develops and supports main points with logically compelling reasons and/or highly persuasive examples; is well focused and well organized; skillfully uses sentence variety and precise vocabulary to convey meaning effectively; demonstrates superior facility with sentence structure and language usage, but may have minor errors that do not interfere with meaning.',
];

const EtsScoreDescription = ({ score }) => {
  const etsScoreDescription = ETS_SCORE_DESCRIPTIONS[Math.ceil(score)];

  return (
    <div className={styles.quoteContainer}>
      <div className={styles.scoreLevelHeader}>Score level description</div>
      <div data-aaa="quoteText" className={styles.quoteText}>
        {etsScoreDescription}
      </div>
      <a
        href="https://www.ets.org/gre/revised_general/prepare/analytical_writing/score_level_descriptions/"
        target="_blank"
        rel="noopener noreferrer"
        className={styles.quoteAttribution}
      >
        Source: ETS&nbsp;<i className="fad fa-external-link" />
      </a>
    </div>
  );
};

class EssayResults extends Component {
  render() {
    const { quizState } = this.props;
    const { essayResponse } = quizState;

    const { score, scoreMeta: scoreMetaString } = essayResponse;

    const scoreMeta = (() => {
      try {
        return JSON.parse(scoreMetaString);
      } catch (e) {
        return {};
      }
    })();

    const feedbackTo = reverse(routes.study.feedback, { productSlug: 'gre' });

    return (
      <div className={styles.layout}>
        <div className={styles.scoreSection}>
          <p className={styles.estimatedScore}>
            Estimated score: <span className={styles.score}>{score}/6</span>
          </p>
          <EtsScoreDescription score={score} />
          <div>
            <p>
              Similar to the application that the ETS uses to score your essay, our
              automated grader uses machine learning.
            </p>
            <p>
              We took a large quantity of hand-graded essays and processed them to derive
              a vector of traits correlating with their scores. This information was used
              to train a predictive model by identifying complex relationships between the
              different traits and scores.
            </p>
            <p>
              After processing your essay in the same way, we ran it through this model to
              generate the score estimate.
            </p>
            <p>
              The score is an estimate, as we don&apos;t have access to the exact methods
              used by ETS. However, if your essay exhibits traits consistent with known
              high-scoring essays, it is likely that yours will be scored highly as well.
            </p>
          </div>
        </div>
        <div className={styles.feedbackSection}>
          <ScoringTraits scoreMeta={scoreMeta} />
          <p>
            Post your essay in our{' '}
            <a
              href="https://talk.achievable.me/c/exam-gre/5"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="fad fa-comments" /> community forum{' '}
              <i className="fad fa-external-link" />
            </a>{' '}
            for some feedback from your peers!
          </p>
          <p>
            Don&apos;t feel the e-grader&apos;s score was accurate?{' '}
            <Link to={feedbackTo}>Send us a note</Link> and we&apos;ll have a human review
            it.
          </p>
        </div>
      </div>
    );
  }
}

export default EssayResults;
